// i18n setup (ADR-0013, design D8): i18next + ICU MessageFormat, English source with fallback.
import i18next, { type Resource } from "i18next"
import ICU from "i18next-icu"
import { createElement, type ReactNode } from "react"
import { I18nextProvider, initReactI18next } from "react-i18next"

export const SUPPORTED_LANGUAGES = ["et", "en", "ru"] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]
export const FALLBACK_LANGUAGE: Language = "en"

export function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

export type I18n = ReturnType<typeof i18next.createInstance>

export function createI18n(resources: Resource, language: Language = FALLBACK_LANGUAGE): I18n {
  const instance = i18next.createInstance()
  void instance
    .use(ICU)
    .use(initReactI18next)
    .init({
      resources,
      lng: language,
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      interpolation: { escapeValue: false },
      returnNull: false,
      initImmediate: false,
    })
  return instance
}

/** Localized message for a problem-details error code; falls back to the server's English title. */
export function errorMessage(t: (key: string, options?: Record<string, unknown>) => string, code: string, title?: string): string {
  const key = `errors.${code}`
  const translated = t(key)
  return translated === key ? (title ?? t("errors.internal.error")) : translated
}

export { useTranslation } from "react-i18next"

/** Provides an instance created by createI18n to the React tree. */
export function I18nProvider({ i18n, children }: { i18n: I18n; children: ReactNode }) {
  return createElement(I18nextProvider, { i18n: i18n as unknown as ProviderProps["i18n"] }, children)
}

type ProviderProps = Parameters<typeof I18nextProvider>[0]
